<template>
    <client-only>
        <v-dialog v-model="modal" max-width="800" scrollable>
            <v-card>
                <v-card-title>
                    <div class="d-flex justify-space-between align-start full-width" style="word-break: break-word">
                        <div class="d-flex flex-column">
                            <span>
                                <span class="text--secondary">
                                    {{ $t('feedback_form_title') }}
                                </span>
                                <span v-if="service" class="text-black ms-1"> {{ service.serviceName }}? </span>
                            </span>
                            <span class="text-caption text-grey">
                                {{ $t('feedback_form_subtitle') }}
                            </span>
                        </div>
                        <v-icon style="margin-top: 5px" class="me-n1 ms-1" @click="modal = false">mdi-close</v-icon>
                    </div>
                </v-card-title>
                <v-card-text>
                    <div v-if="isShowFeedbackAddedMessage" class="full-width text-h5 py-4 font-weight-medium">
                        {{ $t('review_has_already_been_sent') }}
                    </div>
                    <v-form v-else v-model="valid" :disabled="!!serviceReview">
                        <v-row no-gutters>
                            <v-col cols="12">
                                <span class="font-weight-bold text-subtitle-2">
                                    1. {{ $t('feedback_overall_impression') }}
                                </span>
                                <div class="d-flex flex-column">
                                    <v-input :model-value="reviewScore" :rules="[requiredRule('review_score')]">
                                        <v-rating v-model="reviewScore" class="d-flex mb-2" :length="ratingLength">
                                            <template v-slot:item="{isFilled, index, click, isHovered}">
                                                <div class="d-flex flex-column align-center">
                                                    <v-icon
                                                        :color="
                                                            isFilled || isHovered
                                                                ? getColor(1 - index / ratingLength)
                                                                : 'grey-lighten-1'
                                                        "
                                                        :class="{'px-0': isMobile}"
                                                        @click="!!serviceReview ? null : click()"
                                                    >
                                                        {{ isFilled || isHovered ? 'mdi-star' : 'mdi-star-outline' }}
                                                    </v-icon>
                                                    <span
                                                        class="mt-n3"
                                                        style="font-size: 12px"
                                                        :class="{'font-weight-bold text-black': isFilled}"
                                                    >
                                                        {{ index + 1 }}
                                                    </span>
                                                    <span
                                                        v-if="index === 0"
                                                        class="text-error text-caption"
                                                        style="position: absolute; bottom: -8px"
                                                    >
                                                        {{ $t('poor') }}
                                                    </span>
                                                    <span
                                                        v-if="index === ratingLength - 1"
                                                        class="text-success text-caption"
                                                        style="position: absolute; bottom: -8px"
                                                    >
                                                        {{ $t('excellent') }}!
                                                    </span>
                                                </div>
                                            </template>
                                        </v-rating>
                                    </v-input>
                                </div>
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold text-subtitle-2">
                                    2. {{ $t('feedback_recommend') }}
                                </span>
                                <v-radio-group v-model="canRecommend" inline hide-details class="mt-2 mb-4">
                                    <v-radio :label="$t('yes')" :value="true" />
                                    <v-radio :label="$t('no')" :value="false" />
                                </v-radio-group>
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold text-subtitle-2">
                                    3. {{ $t('feedback_what_did_like') }}
                                    <v-icon size="small" color="black"> mdi-thumb-up-outline </v-icon></span
                                >
                                <v-textarea
                                    v-model="positiveFeedback"
                                    variant="outlined"
                                    :placeholder="$t('type_here')"
                                    rows="3"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold text-subtitle-2">
                                    4. {{ $t('feedback_what_did_dislike') }}
                                    <v-icon size="small" color="black"> mdi-thumb-down-outline </v-icon></span
                                >
                                <v-textarea
                                    v-model="negativeFeedback"
                                    variant="outlined"
                                    :placeholder="$t('type_here')"
                                    rows="3"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions v-if="!serviceReview" class="justify-center d-flex">
                    <div class="pb-4">
                        <v-btn
                            :loading="isLoading"
                            :disabled="isLoading || !valid"
                            color="primary"
                            size="small"
                            @click="saveReview"
                        >
                            {{ $t('submit_feedback') }}
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </client-only>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import {EventBus, SERVICE_FEEDBACK_SAVED, SHOW_SERVICE_FEEDBACK_MODAL} from '@/utils/event-bus'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin'

    @Component
    export class ServiceReviewModal extends mixins(FormValidationRulesMixin) {
        modal = false
        valid = false
        isLoading = false
        service = null
        serviceReview = null

        ratingLength = 10

        reviewScore = null
        canRecommend = null
        positiveFeedback = null
        negativeFeedback = null

        isShowFeedbackAddedMessage = false

        mounted() {
            EventBus.$on(SHOW_SERVICE_FEEDBACK_MODAL, this.showModal)
        }

        beforeUnmount() {
            EventBus.$off(SHOW_SERVICE_FEEDBACK_MODAL, this.showModal)
        }

        showModal(service, serviceReview) {
            this.service = service
            this.serviceReview = serviceReview
            this.reset()
            this.modal = true
        }

        reset() {
            this.reviewScore = null
            this.canRecommend = null
            this.positiveFeedback = null
            this.negativeFeedback = null
            if (this.serviceReview) {
                this.reviewScore = this.serviceReview.reviewScore
                this.canRecommend = this.serviceReview.canRecommend
                this.positiveFeedback = this.serviceReview.positiveFeedback
                this.negativeFeedback = this.serviceReview.negativeFeedback
            }
        }

        async saveReview() {
            this.isLoading = true
            try {
                await this.$api.reviews.post({
                    processId: this.service.processId,
                    ...(this.positiveFeedback && {positiveFeedback: this.positiveFeedback}),
                    ...(this.negativeFeedback && {negativeFeedback: this.negativeFeedback}),
                    ...(this.reviewScore !== null && {reviewScore: this.reviewScore}),
                    ...(this.canRecommend !== null && {canRecommend: this.canRecommend}),
                })

                this.$toast.success(this.$t('success_message.saved_successfully'))
                EventBus.$emit(SERVICE_FEEDBACK_SAVED)
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isLoading = false
                this.modal = false
            }
        }

        getColor(value) {
            const hue = ((1 - value) * 120).toString(10)
            return ['hsl(', hue, ',100%, 30%)'].join('')
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default ServiceReviewModal
</script>
